// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {CodeTooltip} from '../CodeBlock/CodeTooltip';
import type {IdentifierResourceType} from './identifierMap';
import {IDENTIFIER_MAP} from './identifierMap';

export type QuoteType = 'none' | 'single' | 'double';

// Render no, single, or double quotes depending on the codeblock language
const format = (quoteType: QuoteType) => (id: string) =>
  ({
    none: id,
    single: `'${id}'`,
    double: `"${id}"`,
  }[quoteType]);

const tooltips = defineMessages({
  order: {
    id: 'docsApp.components.Identifier.order',
    defaultMessage: `Create an Order with the <a>API</a> before making this request.`,
  },
  inventory: {
    id: 'docsApp.components.Identifier.inventory',
    defaultMessage: `Create an Inventory with the <a>API</a> before making this request.`,
  },
  invoice: {
    id: 'docsApp.components.Identifier.invoice',
    defaultMessage: `Create an invoice with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  coupon: {
    id: 'docsApp.components.Identifier.coupon',
    defaultMessage: `Create a Coupon with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  customer: {
    id: 'docsApp.components.Identifier.customer',
    defaultMessage: `Create a Customer with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  paymentMethod: {
    id: 'docsApp.components.Identifier.paymentMethod',
    defaultMessage: `Create a PaymentMethod with the <a1>API</a1> or hardcode a <a2>test ID</a2> before making this request.`,
  },
  price: {
    id: 'docsApp.components.Identifier.price',
    defaultMessage: `Create a Price and Product with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  product: {
    id: 'docsApp.components.Identifier.product',
    defaultMessage: `Create a Price and Product with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  partner: {
    id: 'docsApp.components.Identifier.partner',
    defaultMessage: `Find your Partner ID in the Partners section of the <a>Dashboard</a>.`,
  },
  connectedAccount: {
    id: 'docsApp.components.Identifier.connectedAccount',
    defaultMessage: `Create a Connected Account with the <a1>API</a1> before making this request. It is also possible to create a Connected Account through the <a2>Dashboard</a2>, although some Issuing features are not yet accessible.`,
  },
  location: {
    id: 'docsApp.components.Identifier.location',
    defaultMessage: `Create a Location with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  file: {
    id: 'docsApp.components.Identifier.file',
    defaultMessage: `Create a File with the <a>API</a> before making this request.`,
  },
  dispute: {
    id: 'docsApp.components.Identifier.dispute',
    defaultMessage: `Create a Dispute with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  paymentIntent: {
    id: 'docsApp.components.Identifier.paymentIntent',
    defaultMessage: `Create a PaymentIntent with the <a1>API</a1> or hardcode a <a2>test ID</a2> before making this request.`,
  },
  paymentRefund: {
    id: 'docsApp.components.Identifier.paymentRefund',
    defaultMessage: `Create a Refund with the API or hardcode a test ID before making this request.`,
  },
  taxCode: {
    id: 'docsApp.components.Identifier.taxCode',
    defaultMessage: `This is the generally taxable tax code for electronically supplied services. You can use <a>other tax codes</a> instead.`,
  },
  account: {
    id: 'docsApp.components.Identifier.account',
    defaultMessage: `Find your Merchant ID by visiting your <a>account settings</a>.`,
  },
  session: {
    id: 'docsApp.components.Identifier.session',
    defaultMessage: `Create a Checkout Session with the <a>API</a> before making this request.`,
  },
  verificationSession: {
    id: 'docsApp.components.Identifier.verificationSession',
    defaultMessage: `Create a VerificationSession with the <a>API</a> before making this request.`,
  },
});

const TOOLTIP_CONTENT: {|
  [key: IdentifierResourceType]: React.Node,
|} = {
  order: (
    <FormattedMessage
      {...tooltips.order}
      values={{
        a: (content) => (
          <a href="/docs/api/orders_v2" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  inventory: (
    <FormattedMessage
      {...tooltips.inventory}
      values={{
        a: (content) => (
          <a href="/docs/api/inventories" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  invoice: (
    <FormattedMessage
      {...tooltips.invoice}
      values={{
        a1: (content) => (
          <a href="/docs/api/invoices" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="https://dashboard.stripe.com/test/dashboard" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  customer: (
    <FormattedMessage
      {...tooltips.customer}
      values={{
        a1: (content) => (
          <a href="/docs/api/customers" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="https://dashboard.stripe.com/test/customers" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  paymentMethod: (
    <FormattedMessage
      {...tooltips.paymentMethod}
      values={{
        a1: (content) => (
          <a href="/docs/api/payment_methods" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="/docs/testing#cards" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  price: (
    <FormattedMessage
      {...tooltips.price}
      values={{
        a1: (content) => (
          <a href="/docs/api/prices" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="https://dashboard.stripe.com/test/products" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  product: (
    <FormattedMessage
      {...tooltips.product}
      values={{
        a1: (content) => (
          <a href="/docs/api/products" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="https://dashboard.stripe.com/test/products" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  coupon: (
    <FormattedMessage
      {...tooltips.coupon}
      values={{
        a1: (content) => (
          <a href="/docs/api/coupons" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="https://dashboard.stripe.com/test/coupons" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  partner: (
    <FormattedMessage
      {...tooltips.partner}
      values={{
        a: (content) => (
          <a
            href="https://dashboard.stripe.com/partners/settings"
            target="_blank"
          >
            {content}
          </a>
        ),
      }}
    />
  ),
  connectedAccount: (
    <FormattedMessage
      {...tooltips.connectedAccount}
      values={{
        a1: (content) => (
          <a href="/docs/api/connected_accounts" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a
            href="https://dashboard.stripe.com/test/connect/accounts"
            target="_blank"
          >
            {content}
          </a>
        ),
      }}
    />
  ),
  location: (
    <FormattedMessage
      {...tooltips.location}
      values={{
        a1: (content) => (
          <a href="/docs/api/terminal/locations" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a
            href="https://dashboard.stripe.com/test/terminal/locations"
            target="_blank"
          >
            {content}
          </a>
        ),
      }}
    />
  ),
  file: (
    <FormattedMessage
      {...tooltips.file}
      values={{
        a: (content) => (
          <a href="/docs/api/files" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  dispute: (
    <FormattedMessage
      {...tooltips.dispute}
      values={{
        a1: (content) => (
          <a href="/docs/api/disputes" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="https://dashboard.stripe.com/test/disputes" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  paymentIntent: (
    <FormattedMessage
      {...tooltips.paymentIntent}
      values={{
        a1: (content) => (
          <a href="/docs/api/payment_intents" target="_blank">
            {content}
          </a>
        ),
        a2: (content) => (
          <a href="/docs/testing#cards" target="_blank">
            {content}
          </a>
        ),
      }}
    />
  ),
  paymentRefund: <FormattedMessage {...tooltips.paymentRefund} />,
  taxCode: (
    <FormattedMessage
      {...tooltips.taxCode}
      values={{
        a: (content) => (
          <a target="_blank" href="/docs/tax/tax-codes#available-tax-codes">
            {content}
          </a>
        ),
      }}
    />
  ),
  account: (
    <FormattedMessage
      {...tooltips.account}
      values={{
        a: (content) => (
          <a
            href="https://dashboard.stripe.com/settings/account"
            target="_blank"
          >
            {content}
          </a>
        ),
      }}
    />
  ),
  session: (
    <FormattedMessage
      {...tooltips.session}
      values={{
        a: (content) => (
          <a target="_blank" href="/docs/api/checkout/sessions/create">
            {content}
          </a>
        ),
      }}
    />
  ),
  verificationSession: (
    <FormattedMessage
      {...tooltips.verificationSession}
      values={{
        a: (content) => (
          <a
            target="_blank"
            href="/docs/api/identity/verification_sessions/create"
          >
            {content}
          </a>
        ),
      }}
    />
  ),
};

type IdentifierProps =
  | {|
      type: IdentifierResourceType,
      // HACK: The value is needed as a React prop since this is looked at
      // specifically when the snippet is copied. See renderCode.js for more.
      value: string,
      lang: string,
      quoteType?: QuoteType,
    |}
  | {|
      type: IdentifierResourceType,
      value: string,
      lang?: string,
      quoteType: QuoteType,
    |};

const langToQuoteType: {[lang: string]: QuoteType} = {
  dotnet: 'double',
  java: 'double',
  go: 'double',
  swift: 'double',
  objc: 'double',
  kotlin: 'double',
  bash: 'double',
  curl: 'double',
};

export const Identifier = ({type, value, quoteType, lang}: IdentifierProps) => {
  let quote;
  if (quoteType) {
    quote = quoteType;
  } else if (lang && langToQuoteType[lang]) {
    quote = langToQuoteType[lang];
  } else {
    quote = 'single';
  }
  const formatter = format(quote);
  const tooltipContent = TOOLTIP_CONTENT[type];
  const id = value || IDENTIFIER_MAP[type]?.id;
  if (!tooltipContent) {
    // Ideally we should never get here and special case all the resources we need
    return <span>{formatter('{{RESOURCE_ID}}')}</span>;
  }
  return <CodeTooltip content={tooltipContent}>{formatter(id)}</CodeTooltip>;
};
